import React, { useEffect, useState } from 'react';
import { adminService } from '../../services/api';
import { X, Edit, RefreshCw, Copy, CheckCircle, Server } from 'lucide-react';
import { format } from 'date-fns';
import './Clients.css';

export default function ClientDetailDrawer({ clientId, onClose, onEdit, onRefresh }) {
  const [data, setData]       = useState(null);
  const [copied, setCopied]   = useState(false);
  const [regen, setRegen]     = useState(false);

  useEffect(() => {
    adminService.getClientOverview(clientId).then(r => setData(r.data));
  }, [clientId]);

  const handleRegenKey = async () => {
    if (!window.confirm('Rotate widget API key? Embedded widgets will stop working until updated.')) return;
    setRegen(true);
    await adminService.regenerateWidgetKey(clientId);
    adminService.getClientOverview(clientId).then(r => setData(r.data));
    setRegen(false);
  };

  const copy = (text) => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  if (!data) return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className="client-drawer" onClick={e => e.stopPropagation()}>
        <div style={{ padding: 40, color: 'var(--text-muted)' }}>Loading...</div>
      </div>
    </div>
  );

  const { client, deployments, openTickets, totalTickets, users } = data;
  const amcActive = client.amcEndDate && new Date(client.amcEndDate) > new Date();

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className="client-drawer" onClick={e => e.stopPropagation()}>
        <div className="drawer-header">
          <div>
            <div className="drawer-title">{client.name}</div>
            <div className="drawer-sub">{client.code} · {client.deploymentType}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => onEdit(client)}><Edit size={13} /> Edit</button>
            <button className="icon-btn" onClick={onClose}><X size={16} /></button>
          </div>
        </div>

        <div className="drawer-body">
          {/* Stats row */}
          <div className="drawer-stats">
            <div className="drawer-stat"><span>{openTickets}</span><label>Open Tickets</label></div>
            <div className="drawer-stat"><span>{totalTickets}</span><label>Total Tickets</label></div>
            <div className="drawer-stat"><span>{deployments.length}</span><label>Deployments</label></div>
            <div className="drawer-stat"><span>{users.length}</span><label>Users</label></div>
          </div>

          {/* AMC */}
          <div className="drawer-section">
            <div className="drawer-section-title">AMC</div>
            <div className="detail-grid">
              <div className="detail-row"><span>Tier</span><strong>{client.amcTier?.name}</strong></div>
              <div className="detail-row"><span>Status</span>
                <span className={amcActive ? 'amc-ok' : 'amc-exp'}>{amcActive ? '● Active' : '● Expired'}</span>
              </div>
              <div className="detail-row"><span>Response SLA</span><strong>{client.amcTier?.responseSlaHours}h</strong></div>
              <div className="detail-row"><span>Resolution SLA</span><strong>{client.amcTier?.resolutionSlaDays} days</strong></div>
              <div className="detail-row"><span>Support Hours</span><strong>{client.amcTier?.supportHours}</strong></div>
              {client.amcEndDate && <div className="detail-row"><span>Expires</span><strong>{format(new Date(client.amcEndDate), 'MMM d, yyyy')}</strong></div>}
            </div>
          </div>

          {/* Widget Key */}
          <div className="drawer-section">
            <div className="drawer-section-title">Widget API Key</div>
            <div className="key-display">
              <code>{client.widgetApiKey}</code>
              <button className="icon-btn-sm" onClick={() => copy(client.widgetApiKey)}>{copied ? <CheckCircle size={12} color="var(--success)" /> : <Copy size={12} />}</button>
              <button className="icon-btn-sm" onClick={handleRegenKey} title="Rotate key" disabled={regen}><RefreshCw size={12} /></button>
            </div>
          </div>

          {/* Deployments */}
          {deployments.length > 0 && (
            <div className="drawer-section">
              <div className="drawer-section-title">Deployments</div>
              {deployments.map(d => (
                <div key={d._id} className="dep-row">
                  <Server size={13} />
                  <span>{d.name}</span>
                  <span className="dep-env">{d.environment}</span>
                  <span className={`dep-status dep-status--${d.currentStatus}`}>{d.currentStatus}</span>
                </div>
              ))}
            </div>
          )}

          {/* Users */}
          {users.length > 0 && (
            <div className="drawer-section">
              <div className="drawer-section-title">Client Users</div>
              {users.map(u => (
                <div key={u._id} className="user-row">
                  <div className="avatar sm">{u.name[0].toUpperCase()}</div>
                  <div><div className="user-name">{u.name}</div><div className="user-email">{u.email}</div></div>
                  <span className="user-role">{u.role}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
